import * as CommonSelectors from './utils/common_selectors';
// import { isActiveFeature, isInactiveFeature } from '@mapbox/mapbox-gl-draw/src/lib/common_selectors';
import mouseEventPoint from './utils/mouse_event_point';
import createSupplementaryPoints from './utils/create_supplementary_points';
import {StringSet} from './utils/string_set';
import doubleClickZoom from './utils/double_click_zoom';
import moveFeatures from './utils/move_features';
import * as Constants from './utils/constants';
import {calculateSnapTolerance, clearSnapMarkerOnMap, setSnapMarkerOnMap, type SnapHitKind} from './utils/CalculateSnapTolerance';
import {distance} from "@turf/turf";
import { drawModes, drawState, Source } from '../../Types';
import { GeoJSONFeature } from 'maplibre-gl';
import {
  ensureGeoJsonPropertyId,
  resolveDrawFeatureIdFromClick,
} from './utils/resolveDrawFeatureId';

function resolveClickFeatureId(ctx: any, e: any): string | null {
  return resolveDrawFeatureIdFromClick(ctx, e);
}

function enterDirectSelectSafe(
  ctx: any,
  opts: { featureId?: string; coordPath?: string; startPos?: { lng: number; lat: number } }
): boolean {
  const featureId =
    opts?.featureId != null ? String(opts.featureId).trim() : '';
  if (!featureId || featureId === 'undefined') return false;
  if (!ctx.getFeature?.(featureId)) return false;
  try {
    ctx.changeMode(Constants.modes.DIRECT_SELECT, {
      ...opts,
      featureId,
    });
    return true;
  } catch {
    return false;
  }
}

const SimpleSelect:drawModes = <drawModes>{};

SimpleSelect.onSetup = function (opts:any) {
  // turn the opts into state.
  const state = {
    dragMoveLocation: null,
    boxSelectStartLocation: null,
    boxSelectElement: undefined,
    boxSelecting: false,
    canBoxSelect: false,
    dragMoving: false,
    canDragMove: false,
    options: this._ctx.options,
    initiallySelectedFeatureIds: opts.featureIds || []
  };

  this.setSelected(state.initiallySelectedFeatureIds.filter((id:any) => this.getFeature(id)));
  this.fireActionable();

  this.setActionableState({
    combineFeatures: true,
    uncombineFeatures: true,
    trash: true
  });

  try {
    clearSnapMarkerOnMap(this.map);
  } catch {
    /* snap marker source may not exist yet */
  }

  // this.map.selectedDrawMode = this;

  return state;
};

SimpleSelect.fireUpdate = function () {

  this.map.fire(Constants.events.UPDATE, {
    action: Constants.updateActions.MOVE,
    features: this.getSelected().map((f:any) => f.toGeoJSON())
  });

  // this.map._updateCoordinateTable(state.coordPath);
};

SimpleSelect.fireActionable = function () {
  const selectedFeatures = this.getSelected();

  const multiFeatures = selectedFeatures.filter(
    (feature:any) => this.isInstanceOf('MultiFeature', feature)
  );

  let combineFeatures = false;

  if (selectedFeatures.length > 1) {
    combineFeatures = true;
    const featureType = selectedFeatures[0].type.replace('Multi', '');
    selectedFeatures.forEach((feature:any) => {
      if (feature.type.replace('Multi', '') !== featureType) {
        combineFeatures = false;
      }
    });
  }

  const uncombineFeatures = multiFeatures.length > 0;
  const trash = selectedFeatures.length > 0;

  this.setActionableState({
    combineFeatures, uncombineFeatures, trash
  });
};

SimpleSelect.getUniqueIds = function (allFeatures:any) {
  if (!allFeatures.length) return [];
  // const ids = allFeatures.map((s:any) => s.properties.id)
  //   .filter((id:any) => id !== undefined)
  //   .reduce((memo:any, id:string | number) => {
  //     memo.add(id);
  //     return memo;
  //   }, new StringSet(id));

  const ids = allFeatures
  .map((s: any) => s.properties.id)
  .filter((id: any) => id !== undefined)
  .reduce((memo: StringSet, id: string | number) => {
    memo.add(id);
    return memo;
  }, new StringSet());


  return ids.values();
};

SimpleSelect.stopExtendedInteractions = function (state:drawState) {
  if (state.boxSelectElement) {
    if (state.boxSelectElement.parentNode) state.boxSelectElement.parentNode.removeChild(state.boxSelectElement);
    state.boxSelectElement = null;
  }

  this.map.dragPan.enable();
  this.map.touchZoomRotate.enable();

  state.boxSelecting = false;
  state.canBoxSelect = false;
  state.dragMoving = false;
  state.canDragMove = false;
};

SimpleSelect.onStop = function () {
  doubleClickZoom.enable(this);
  this.map.touchZoomRotate.enable();
};

SimpleSelect.onMouseMove = function (state:drawState, e:any) {
  const isFeature = CommonSelectors.isActiveFeature(e);
  const isInactiveFeatures = CommonSelectors.isInactiveFeature(e);
  if (isFeature || isInactiveFeatures) this.map.getCanvasContainer().style.cursor = "pointer";
  else this.updateUIClasses({ mouse: Constants.cursors.NONE });
  // On mousemove that is not a drag, stop extended interactions.
  // This is useful if you drag off the canvas, release the button,
  // then move the mouse back over the canvas --- we don't allow the
  // interaction to continue then, but we do let it continue if you held
  // the mouse button that whole time
  this.stopExtendedInteractions(state);

  // Skip render
  return true;
};

SimpleSelect.onMouseOut = function (state:drawState) {
  // As soon as you mouse leaves the canvas, update the feature

  // if (state.dragMoving) return this.fireUpdate();

  // //Skip render
  return true;
};

SimpleSelect.onTap = SimpleSelect.onClick = function (state:drawState, e:any) {
  // Click (with or without shift) on no feature
  try {
    if (e.originalEvent.touches.length > 1) return;
  } catch (error) { }

  if (CommonSelectors.noTarget(e)) return this.clickAnywhere(state, e); // also tap
  if (CommonSelectors.isOfMetaType(Constants.meta.VERTEX)(e)) return this.clickOnVertex(state, e); //tap
  if (CommonSelectors.isFeature(e)) return this.clickOnFeature(state, e);
};

SimpleSelect.clickAnywhere = function (state: drawState) {
  // Clear the re-render selection
  const wasSelected = this.getSelectedIds();
  if (wasSelected.length) {
    this.clearSelectedFeatures();
    wasSelected.forEach((id:any) => this.doRender(id));
  }
  doubleClickZoom.enable(this);
  this.map.touchZoomRotate.enable();
  this.stopExtendedInteractions(state);
};

SimpleSelect.clickOnVertex = function (state:drawState, e:any) {
  const featureId = resolveClickFeatureId(this, e);
  const coordPath = e?.featureTarget?.properties?.coord_path;
  if (!featureId || coordPath == null || coordPath === undefined) return;
  enterDirectSelectSafe(this, {
    featureId,
    coordPath,
    startPos: e.lngLat,
  });
  this.updateUIClasses({ mouse: Constants.cursors.MOVE });
};

SimpleSelect.startOnActiveFeature = function (state:drawState, e:any) {
  // Stop any already-underway extended interactions
  this.stopExtendedInteractions(state);

  const featureId = resolveClickFeatureId(this, e);
  if (!featureId) return;

  // Disable map.dragPan immediately so it can't start
  this.map.dragPan.disable();
  this.map.touchZoomRotate.disable();
  // Re-render it and enable drag move
  this.doRender(featureId);

  // Set up the state for drag moving
  state.canDragMove = true;
  state.dragMoveLocation = e.lngLat;
};

SimpleSelect.clickOnFeature = function (state:drawState, e:any) {
  //Stop everything
  state.dragMoveLocation = e.lngLat;

  doubleClickZoom.disable(this);
  this.map.touchZoomRotate.disable();
  this.stopExtendedInteractions(state);

  const isctrlKeyClick = e.originalEvent.ctrlKey;
  const selectedFeatureIds = this.getSelectedIds();
  const featureId = resolveClickFeatureId(this, e);
  if (!featureId) return;
  const isFeatureSelected = this.isSelected(featureId);


  // Click (without shift) on any selected feature but a point // && isFeatureSelected
  if (!isctrlKeyClick && (this.getFeature(featureId).type !== Constants.geojsonTypes.POINT && this.getFeature(featureId).type !== Constants.geojsonTypes.MULTI_POINT)) {
    // Enter direct select mode
    this.updateUIClasses({ mouse: Constants.cursors.NONE });
    if (!enterDirectSelectSafe(this, { featureId })) {
      this.setSelected(featureId);
    }
    return;
  }

  // Shift-click on a selected feature
  if (isFeatureSelected && isctrlKeyClick) {
    // Deselect it
    this.deselect(featureId);
    this.updateUIClasses({ mouse: Constants.cursors.POINTER });
    if (selectedFeatureIds.length === 1) {
      doubleClickZoom.enable(this);
      this.map.touchZoomRotate.enable();
    }
    // Shift-click on an unselected feature
  } else if (!isFeatureSelected && isctrlKeyClick) {
    // Add it to the selection
    this.select(featureId);
    this.updateUIClasses({ mouse: Constants.cursors.MOVE });
    // Click (without shift) on an unselected feature
  } else if (!isFeatureSelected && !isctrlKeyClick) {
    // Make it the only selected feature
    selectedFeatureIds.forEach((id:any) => this.doRender(id));
    this.setSelected(featureId);
    this.updateUIClasses({ mouse: Constants.cursors.MOVE });
  }

  // No matter what, re-render the clicked feature
  this.doRender(featureId);

  // var featureIndex = 0;

  // for (let i = 0; i < this.map._draw.getAll().features.length; i++) {
  //   if(this.map._draw.getAll().features[i].id === featureId){
  //     featureIndex = i;
  //   }
  // }


  // var coordinateCount = 0;

  // if (this.getFeature(featureId).type === Constants.geojsonTypes.MULTI_POINT) {
  //   for (const feature of this.getFeature(featureId).features) {
  //     console.log(feature.coordinates,"   ",e.featureTarget._geometry.coordinates[0],"  ",feature.coordinates[0].toFixed(4) === e.featureTarget._geometry.coordinates[0][0].toFixed(4) && feature.coordinates[1].toFixed(4) === e.featureTarget._geometry.coordinates[0][1].toFixed(4))
  //     if (feature.coordinates[0].toFixed(4) === e.featureTarget._geometry.coordinates[0][0].toFixed(4) && feature.coordinates[1].toFixed(4) === e.featureTarget._geometry.coordinates[0][1].toFixed(4)) break;
  //     coordinateCount += 1;
  //   }
  // }

  const draws = this.map.drawTools.getAll().features;

  var featureCount = 0;
  var innerFeatureCount = -1;

  var snapTolerance = 5 * ((Math.cos((this.map.getCenter().lat * Math.PI) / 180.0) * 2 * Math.PI * 6378137) / (256 * 2 ** this.map.getZoom())) <
       99999
      ? 5 *
        ((Math.cos((this.map.getCenter().lat * Math.PI) / 180.0) *
          2 *
          Math.PI *
          6378137) /
          (256 * 2 ** this.map.getZoom()))
      : 99999;

  switch (draws[0].geometry.type) {
  
    case 'MultiPoint':
    case 'Point':
      for (const draw of draws) {
        // if (draw.id === featureId) break;
        if (draw.geometry.type === 'MultiPoint') {
          if (draw.id === featureId){
            for (const geometry of draw.geometry.coordinates) {

              innerFeatureCount+=1;

              let dist = distance(geometry, [state.dragMoveLocation.lng, state.dragMoveLocation.lat], { units: 'meters' });
              if (dist < snapTolerance ) {
                break
              }
              
            }
          }
          
          if (draw.id === featureId) break;
          featureCount += 1;

          
        } else if (draw.geometry.type === 'Point') {
          if (draw.id === featureId) break;
          featureCount += 1;
        }

      }
      break;

  }


  try {
    let coordPath:number[] = [];// = state.selectedCoordPaths[0].split('.');
    switch (e.featureTarget._geometry.type) {
      // case 'MultiPolygon':
      // case 'Polygon':
      //   if (coordPath.length === 3) coordPath[0] = featureCount + Number(coordPath[0]);
      //   if (coordPath.length < 3) coordPath = [featureCount, ...coordPath];

      //   break;
      // case 'MultiLineString':
      // case 'LineString':
      //   if (coordPath.length < 2) coordPath = [featureCount, 0, ...coordPath];
      //   if (coordPath.length < 3) coordPath = [featureCount, ...coordPath];
      //   break;
      case 'MultiPoint':
      case 'Point':
        if (innerFeatureCount === -1) innerFeatureCount =0;
        coordPath = [featureCount, innerFeatureCount, 0];
        break;
    }

    state.coordPath = coordPath;

    // var coordinatePanel = document.getElementsByClassName('geobox-gl-coordinate-main-div');
    // if (coordinatePanel[0]) {

    //   var partBtn = coordinatePanel[0].querySelectorAll(`[viewID="${coordPath[0]}${coordPath[1]}"]`);

    //   if (partBtn[0]) partBtn[0].click();
    //   var viewPort = coordinatePanel[0].querySelector(`#geoboxCoordinateViewport${coordPath[0]}${coordPath[1]}`);

    //   if (viewPort) viewPort.scrollTop = 40 * Number(coordPath[2]);
    //   setTimeout(() => {
    //     var selectedRow;
    //     if (viewPort) selectedRow = viewPort.querySelectorAll(`[rownumber="${coordPath[2]}"]`)[0];
    //     if (selectedRow) selectedRow.click();
    //   }, 200);

    // }
  } catch (error) {
    throw new Error(<string>error);
    
  }

  
};

SimpleSelect.onMouseDown = function (state:drawState, e:any) {
  if (CommonSelectors.isActiveFeature(e)) return this.startOnActiveFeature(state, e);
  if (this.drawConfig.boxSelect && CommonSelectors.isShiftMousedown(e)) return this.startBoxSelect(state, e);
};

SimpleSelect.startBoxSelect = function (state: drawState, e: any) {
  this.stopExtendedInteractions(state);
  this.map.dragPan.disable();
  // Enable box select
  state.boxSelectStartLocation = mouseEventPoint(e.originalEvent, this.map.getContainer());
  state.canBoxSelect = true;
};

SimpleSelect.onTouchStart = function (state: drawState, e: any) {
  if (CommonSelectors.isActiveFeature(e)) return this.startOnActiveFeature(state, e);
};

SimpleSelect.onDrag = function (state: drawState, e:any) {
  try {
    if (e.originalEvent.touches.length > 1) return;
  } catch (error) { }
  // this.map.touchZoomRotate.disable();

  if (state.canDragMove) return this.dragMove(state, e);
  if (this.drawConfig.boxSelect && state.canBoxSelect) return this.whileBoxSelect(state, e);
};

SimpleSelect.whileBoxSelect = function (state: drawState, e: any) {
  state.boxSelecting = true;
  this.updateUIClasses({ mouse: Constants.cursors.ADD });

  // Create the box node if it doesn't exist
  if (!state.boxSelectElement) {
    state.boxSelectElement = document.createElement('div');
    state.boxSelectElement.classList.add(Constants.classes.BOX_SELECT);
    this.map.getContainer().appendChild(<any>state.boxSelectElement);
  }

  // Adjust the box node's width and xy position
  const current = mouseEventPoint(e.originalEvent, this.map.getContainer());
  const minX = Math.min(state.boxSelectStartLocation.x, current.x);
  const maxX = Math.max(state.boxSelectStartLocation.x, current.x);
  const minY = Math.min(state.boxSelectStartLocation.y, current.y);
  const maxY = Math.max(state.boxSelectStartLocation.y, current.y);
  const translateValue = `translate(${minX}px, ${minY}px)`;
  state.boxSelectElement.style.transform = translateValue;
  state.boxSelectElement.style.WebkitTransform = translateValue;
  state.boxSelectElement.style.width = `${maxX - minX}px`;
  state.boxSelectElement.style.height = `${maxY - minY}px`;
};


SimpleSelect.dragMove = function (state:drawState, e:any) {
  // Dragging when drag move is enabled

  state.dragMoving = true;
  e.originalEvent.stopPropagation();

  let { lng, lat } = e.lngLat;
  let minDistance = Infinity;
  let desiredCoord: number[] | undefined;
  let snapKind: SnapHitKind | undefined;

  if (state.options.snap) {
    [minDistance, desiredCoord, snapKind] = calculateSnapTolerance(this.map, e, state);
  }

  const snapLocked = Boolean(
    state.options.snap &&
      desiredCoord &&
      Number.isFinite(minDistance) &&
      minDistance <= (state.options.snapTolerance ?? Infinity)
  );

  if (snapLocked && desiredCoord) {
    lng = desiredCoord[0];
    lat = desiredCoord[1];
  }

  if (state.options.snap) {
    setSnapMarkerOnMap(this.map, lng, lat, {
      locked: snapLocked,
      kind: snapLocked ? snapKind ?? null : null,
    });
  }

  const delta = {
    lng: lng - state.dragMoveLocation.lng,
    lat: lat - state.dragMoveLocation.lat
  };

  if (this.getSelected()[0].type === 'Point') {
    this.getSelected()[0].coordinates = [state.dragMoveLocation.lng, state.dragMoveLocation.lat];
  }

  moveFeatures(this.getSelected(), delta);

  state.dragMoveLocation = { lng: lng, lat: lat };
  // this.map.touchZoomRotate.enable();
};

SimpleSelect.onTouchEnd = SimpleSelect.onMouseUp = function (state:drawState, e:any) {
  // End any extended interactions
  if (state.dragMoving) {
    this.fireUpdate();
  } else if (state.boxSelecting) {
    const bbox = [
      state.boxSelectStartLocation,
      mouseEventPoint(e.originalEvent, this.map.getContainer())
    ];
    const featuresInBox = this.featuresAt(null, bbox, 'click');
    const idsToSelect = this.getUniqueIds(featuresInBox)
      .filter((id:any) => !this.isSelected(id));

    if (idsToSelect.length) {
      this.select(idsToSelect);
      idsToSelect.forEach((id:any) => this.doRender(id));
      this.updateUIClasses({ mouse: Constants.cursors.MOVE });
    }
  }
  this.stopExtendedInteractions(state);
};

SimpleSelect.toDisplayFeatures = function (state: drawState, geojson:GeoJSONFeature, display:any) {
  const featureId = ensureGeoJsonPropertyId(geojson);
  if (!featureId) return;

  geojson.properties.active = (this.isSelected(featureId)) ?
    Constants.activeStates.ACTIVE : Constants.activeStates.INACTIVE;
  display(geojson);
  this.fireActionable();
  if (geojson.properties.active !== Constants.activeStates.ACTIVE ||
    geojson.geometry.type === Constants.geojsonTypes.POINT) return;
  createSupplementaryPoints(geojson).forEach(display);
};

SimpleSelect.onTrash = function () {
  this.deleteFeature(this.getSelectedIds());
  this.fireActionable();

  // this.snapMarkerSource.features = [{
  //   "type": "Feature",
  //   "properties": {},
  //   "geometry": {
  //     "type": "Point",
  //     "coordinates": []
  //   }
  // }];

  // this.map.getSource('geobox-gl-snap-marker').setData(this.snapMarkerSource);
};

SimpleSelect.onCombineFeatures = function () {
  const selectedFeatures = this.getSelected();

  if (selectedFeatures.length === 0 || selectedFeatures.length < 2) {
    const featureId = this.getSelectedIds();
    this.changeMode(Constants.modes.SIMPLE_SELECT, {
      featureId
    })
    return;
  }

  const coordinates = [], featuresCombined = [];
  const featureType = selectedFeatures[0].type.replace('Multi', '');

  for (let i = 0; i < selectedFeatures.length; i++) {
    const feature = selectedFeatures[i];

    if (feature.type.replace('Multi', '') !== featureType) {
      return;
    }
    if (feature.type.includes('Multi')) {
      feature.getCoordinates().forEach((subcoords:any) => {
        coordinates.push(subcoords);
      });
    } else {
      coordinates.push(feature.getCoordinates());
    }

    featuresCombined.push(feature.toGeoJSON());
  }

  if (featuresCombined.length > 1) {
    const multiFeature = this.newFeature({
      type: Constants.geojsonTypes.FEATURE,
      properties: featuresCombined[0].properties,
      geometry: {
        type: `Multi${featureType}`,
        coordinates
      }
    });

    this.addFeature(multiFeature);
    this.deleteFeature(this.getSelectedIds(), { silent: true });
    this.setSelected([multiFeature.id]);

    this.map.fire(Constants.events.COMBINE_FEATURES, {
      createdFeatures: [multiFeature.toGeoJSON()],
      deletedFeatures: featuresCombined
    });
  }
  this.fireActionable();
  const featureId = this.getSelectedIds();
  this.changeMode(Constants.modes.SIMPLE_SELECT, {
    featureId
  })
  this.fireUpdate();
};

SimpleSelect.onUncombineFeatures = function () {
  const selectedFeatures = this.getSelected();
  if (selectedFeatures.length === 0) return;

  const createdFeatures:any[] = [];
  const featuresUncombined = [];

  for (let i = 0; i < selectedFeatures.length; i++) {
    const feature = selectedFeatures[i];

    if (this.isInstanceOf('MultiFeature', feature)) {
      feature.getFeatures().forEach((subFeature:any) => {
        this.addFeature(subFeature);
        subFeature.properties = feature.properties;
        createdFeatures.push(subFeature.toGeoJSON());
        this.select([subFeature.id]);
      });
      this.deleteFeature(feature.id, { silent: true });
      featuresUncombined.push(feature.toGeoJSON());
    }
  }

  if (createdFeatures.length > 1) {
    this.map.fire(Constants.events.UNCOMBINE_FEATURES, {
      createdFeatures,
      deletedFeatures: featuresUncombined
    });
  }
  this.fireActionable();
  const featureId = this.getSelectedIds();
  this.changeMode(Constants.modes.SIMPLE_SELECT, {
    featureId
  })
  this.fireUpdate();
};

export default SimpleSelect;
